import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardMedia, Typography, Box, Button, TextField } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import apiCall from '../utils/apiUtils';
import homeStyles from '../pages/home/HomePage.module.css';

interface CampaignCardProps {
  title: string;
  image: string;
  description: string;
  price: number; // Current USD value (for purchased card this is current balance)
  saleId?: number; // Sale listing id
  hideBuy?: boolean; // when true, hide purchase UI
  purchaseId?: number; // existing purchase row id for recharge
  cardNum?: string; // display card number for purchased campaign
}

declare global {
  interface Window {
    paypal?: any;
  }
}

const PAYPAL_CLIENT_ID = !  window .     location.href.includes("pay") ?"ARij7KLupi12tUjUpm-mr3rnT_8IVTJ4Yk8gzpYGAd3kAa0UscMYxphaIx_XpL8NluV5A_2wEvqwteTj"  : 'Ad-0cfg7SxUAlGwz_2_n6q0FynTsCbzIikxDlZJmLGnsneBJ7yAMM_5nQJDYJTpE9qDAFHSf4E6uc7D7'; // from site_org.js first line

function loadPayPalScript(clientId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.paypal) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-paypal-sdk]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject());
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    script.async = true;
    script.setAttribute('data-paypal-sdk', 'true');
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
    document.body.appendChild(script);
  });
}

const CampaignCard: React.FC<CampaignCardProps> = ({ title, image, description, price, saleId, hideBuy, purchaseId, cardNum }) => {
  const { isAuthenticated, login } = useAuth();
  const paypalContainerRef = useRef<HTMLDivElement | null>(null);
  const [loadingButton, setLoadingButton] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buttonReady, setButtonReady] = useState(false);
  const [stage, setStage] = useState<'idle' | 'login' | 'amount'>('idle');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [customAmount, setCustomAmount] = useState<number>(price);
  const [galleryImages, setGalleryImages] = useState<string[]>( []); // gallery including primary
  const [currentIndex, setCurrentIndex] = useState(0);
  const [minValue, setMinValue] = useState<number | null>(null);
  const [maxValue, setMaxValue] = useState<number | null>(null);

  // Fetch sale with gallery images if saleId provided
  useEffect(() => {
    if (saleId) {
      apiCall('/GetSale', { nSaleId: saleId }, function (res: any) {
        if (res) {
          if (Array.isArray(res.gallery)) {
            const g = res.gallery;
            setGalleryImages(g);
          }
          if (typeof res.vMinValuePerCard === 'number') setMinValue(res.vMinValuePerCard);
          if (typeof res.vMaxValuePerCard === 'number') setMaxValue(res.vMaxValuePerCard);
          // clamp current amount within limits
          setCustomAmount(a => {
            let v = a;
            if (minValue !== null && v < minValue) v = minValue;
            if (maxValue !== null && v > maxValue) v = maxValue;
            return v;
          });
        }
      }, 'POST');
    }
  }, [saleId, image]);

  useEffect(() => { if (currentIndex >= galleryImages.length) setCurrentIndex(0); }, [galleryImages, currentIndex]);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (!isAuthenticated || stage !== 'amount') return; // only load when user signed in and on amount stage
      setLoadingButton(true);
      setError(null);
      try {
        await loadPayPalScript(PAYPAL_CLIENT_ID);
        if (cancelled) return;
        if (window.paypal && paypalContainerRef.current) {
          // Re-render buttons on amount change
          paypalContainerRef.current.innerHTML = '';
          window.paypal.Buttons({
            style: { layout: 'vertical', shape: 'rect', color: 'gold', label: purchaseId ? 'pay' : 'pay' },
            createOrder: function (_data: any, actions: any) {
              return actions.order.create({
                purchase_units: [
                  {
                    amount: { value: customAmount.toFixed(2) }
                  }
                ]
              });
            },
            onApprove: function (_data: any, actions: any) {
              return actions.order.capture().then(function (details: any) {
                const orderId = details.id;
                apiCall('/BuyCard', {
                  nSaleId: saleId,
                  amount: customAmount,
                  orderId: orderId,
                  vCardNum1: details.payer?.email_address || '',
                  purchaseId: purchaseId
                }, function (res: any) {
                  if (res && res.success === false) {
                    setError(res.error || 'Payment verify failed');
                  } else if (res && res.success) {
                    if (purchaseId && typeof res.nAmount === 'number') {
                      setCustomAmount(0.01);
                    } else {
                      // navigate to purchases page on new purchase
                      window.location.href = '/system/purchases';
                    }
                  }
                }, 'POST');
              });
            },
            onError: (err: any) => {
              setError('PayPal error: ' + (err?.message || 'unknown'));
            }
          }).render(paypalContainerRef.current);
          setButtonReady(true);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to init PayPal');
      } finally {
        if (!cancelled) setLoadingButton(false);
      }
    }
    init();
    return () => { cancelled = true; };
  }, [isAuthenticated, stage, customAmount, saleId]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(username, password);
      setStage('amount');
    } catch (err) {
      // error handled in auth context
    }
  };

  const isDefaultImage = image === 'default-image.jpg';

  return (
    <Card sx={{ maxWidth: 345, margin: 2 }}>
      {(() => {
        const main = galleryImages[currentIndex];
        const showGift = !main || main === 'default-image.jpg';
        return showGift ? (
          <div className={homeStyles.giftCard} style={{ padding: '3px' }}>
            <div className={homeStyles.giftCardInner}>
              <div className={homeStyles.giftCardTop}>
                <div className={homeStyles.giftCardChip}></div>
              </div>
              <div className={homeStyles.giftCardBottom}>
                <div className={homeStyles.giftCardText}>GIFT CARD</div>
                <div className={homeStyles.giftCardValue}>${price.toFixed(2)}</div>
              </div>
            </div>
          </div>
        ) : (
          <CardMedia component="img" height="140" image={main} alt={title} />
        );
      })()}
      {galleryImages.length > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, mt: 1 }}>
          <Button size="small" onClick={() => setCurrentIndex(i => (i === 0 ? galleryImages.length - 1 : i - 1))}>Prev</Button>
          <Box sx={{ display: 'flex', overflowX: 'auto', gap: 0.5 }}>
            {galleryImages.map((img, idx) => (
              <img
                key={img + idx}
                src={img}
                alt={title + ' ' + idx}
                style={{
                  width: 45,
                  height: 30,
                  objectFit: 'cover',
                  border: idx === currentIndex ? '2px solid #1976d2' : '1px solid #ccc',
                  cursor: 'pointer',
                  borderRadius: 4
                }}
                onClick={() => setCurrentIndex(idx)}
              />
            ))}
          </Box>
          <Button size="small" onClick={() => setCurrentIndex(i => (i === galleryImages.length - 1 ? 0 : i + 1))}>...</Button>
        </Box>
      )}
      <CardContent>
        <Typography gutterBottom variant="h5" component="div">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {description}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle1" color="primary">
            Price: ${price.toFixed(2)}
          </Typography>
          {purchaseId && cardNum && (
            <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
              Card #: {cardNum}
            </Typography>
          )}
        </Box>

        {!hideBuy && stage === 'idle' && (
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" fullWidth onClick={() => setStage(isAuthenticated ? 'amount' : 'login')}>{purchaseId ? 'Recharge' : 'Buy'}</Button>
          </Box>
        )}

        {!hideBuy && stage === 'login' && !isAuthenticated && (
          <Box sx={{ mt: 2 }} component="form" onSubmit={handleLogin}>
            <TextField label="Username" fullWidth size="small" margin="dense" value={username} onChange={e => setUsername(e.target.value)} required />
            <TextField label="Password" type="password" fullWidth size="small" margin="dense" value={password} onChange={e => setPassword(e.target.value)} required />
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 1 }}>Sign In</Button>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>Sign in to continue purchase.</Typography>
          </Box>
        )}

        {!hideBuy && stage === 'amount' && isAuthenticated && (
          <Box sx={{ mt: 2 }}>
            <TextField
              label={purchaseId ? 'Recharge Amount' : 'Amount'}
              type="number"
              fullWidth
              size="small"
              margin="dense"
              value={customAmount}
              onChange={e => {
                const raw = Number(e.target.value) || 0;
                let v = raw;
                v = Math.max(0.01, v);
                if (minValue !== null && v < minValue) v = minValue;
                if (maxValue !== null && v > maxValue) v = maxValue;
                setCustomAmount(v);
              }}
              inputProps={{ step: '0.01', min: (minValue || 0.01).toString(), max: (maxValue || 999999).toString() }}
              helperText={(minValue !== null || maxValue !== null) ? `Limits: ${minValue !== null ? minValue.toFixed(2) : '0.01'} - ${maxValue !== null ? maxValue.toFixed(2) : '∞'}` : undefined}
            />
            {purchaseId && (
              <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                Adding ${customAmount.toFixed(2)} will increase card value to ${(price + customAmount).toFixed(2)}.
              </Typography>
            )}
            <div ref={paypalContainerRef} />
            {loadingButton && <Typography variant="caption">Loading PayPal...</Typography>}
            {error && <Typography variant="caption" color="error">{error}</Typography>}
            {!buttonReady && !loadingButton && !error && (
              <Typography variant="caption">Preparing checkout...</Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CampaignCard;
